import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ChevronRight,
  RefreshCcw,
  Search as SearchIcon,
  ImageOff,
  AlertTriangle,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { AppHeader } from "@/components/app/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type DateRange } from "@/components/common/DateRangeFilter";
import { AdsDateRangePicker } from "@/components/common/AdsDateRangePicker";
import { fetchAdsSheet } from "@/lib/ads-sheets.functions";
import { cn } from "@/lib/utils";
import { todayIstIso } from "@/lib/tz";

const TODAY_RANGE = (): DateRange => {
  const t = todayIstIso();
  return { from: t, to: t };
};

const ACCENT = "#18181b";

const CAMPAIGN_COLS = [
  { key: "results", label: "Results", w: 64 },
  { key: "cpa", label: "Cost/Purch", w: 80 },
  { key: "spent", label: "Spent", w: 88 },
  { key: "budget", label: "Budget", w: 72 },
  { key: "cpm", label: "CPM", w: 72 },
  { key: "ctr", label: "CTR", w: 64 },
  { key: "cpc", label: "CPC", w: 64 },
  { key: "clicks", label: "Clicks", w: 64 },
  { key: "lpv", label: "Landing", w: 72 },
  { key: "cplpv", label: "Cost/LPV", w: 80 },
] as const;
const CAMPAIGN_MIN_WIDTH = 1200;
const STICKY_NAME_MIN = 280;

// Facebook Ads Manager palette (scoped to this page only).
const FB = {
  bg: "#F0F2F5",
  card: "#FFFFFF",
  border: "#E4E6EA",
  text: "#1C1E21",
  muted: "#65676B",
  empty: "#BCC0C4",
  blue: "#1877F2",
  blueSoft: "#E7F3FF",
  green: "#1A7F37",
  greenDot: "#42B72A",
  hover: "#F2F4F7",
  altRow: "#F7F8FA",
  shadow: "0 1px 2px rgba(0,0,0,0.1)",
  cardShadow: "0 1px 4px rgba(0,0,0,0.1)",
  // Summary (dark)
  darkLabel: "#B0B3B8",
  darkShopify: "#6BD672",
  darkMeta: "#74B3FE",
};

export const Route = createFileRoute("/_app/ads-performance")({
  component: AdsPerformancePage,
});

// ----------------- Permission gate -----------------
function useAdsAccess() {
  const { role, profile } = useAuth();
  const roleKey =
    role === "admin"
      ? "admin"
      : role === "moderator"
        ? "moderator"
        : profile?.department === "graphic_designer"
          ? "designer"
          : (profile?.department ?? null);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!roleKey) {
      setAllowed(false);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from("ads_section_permissions")
        .select("can_access")
        .eq("role", roleKey)
        .maybeSingle();
      if (!cancelled) setAllowed(!!data?.can_access);
    })();
    return () => {
      cancelled = true;
    };
  }, [roleKey]);
  return allowed;
}

// ----------------- Page -----------------
function AdsPerformancePage() {
  const allowed = useAdsAccess();
  const [tab, setTab] = useState<"campaigns" | "orders" | "spent">("campaigns");

  if (allowed === null)
    return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!allowed) return <Navigate to="/dashboard" />;

  return (
    <>
      <AppHeader subtitle="Ads Performance — Google Sheets data" />
      <div style={{ background: FB.bg, minHeight: "calc(100vh - 60px)" }}>
        <div className="px-[22px] py-3">
          <div
            className="mb-1 text-[20px] font-bold"
            style={{ color: FB.text, fontWeight: 700 }}
          >
            Ads Performance
          </div>
          <div
            className="mb-3 flex flex-wrap gap-0 border-b"
            style={{ borderColor: FB.border }}
          >
            {(
              [
                { k: "campaigns", l: "Campaigns" },
                { k: "orders", l: "Orders" },
                { k: "spent", l: "Daily Ad Spent" },
              ] as const
            ).map((t) => {
              const active = tab === t.k;
              return (
                <button
                  key={t.k}
                  onClick={() => setTab(t.k)}
                  className="h-10 px-4 text-[14px] font-semibold transition-colors"
                  style={{
                    color: active ? FB.blue : FB.muted,
                    borderBottom: active
                      ? `2px solid ${FB.blue}`
                      : "2px solid transparent",
                    marginBottom: "-1px",
                  }}
                >
                  {t.l}
                </button>
              );
            })}
          </div>
          {tab === "campaigns" && <CampaignsTab />}
          {tab === "orders" && <OrdersTab />}
          {tab === "spent" && <DailySpentTab />}
        </div>
      </div>
    </>
  );
}

// ----------------- Hooks for sheet fetching -----------------
// Per-sheet TTL — Google Sheets API has ~60 reads/min limit, so cache aggressively.
const SHEET_TTL_MS: Record<string, number> = {
  campaigns_hourly: 5 * 60_000,
  campaigns_daily: 30 * 60_000,
  ad_spent_daily: 30 * 60_000,
  shopify_orders_daily: 30 * 60_000,
  shopify_orders_triggers: 5 * 60_000,
};

function isRateLimitError(err: unknown): boolean {
  if (!err) return false;
  const msg = String((err as Error).message ?? err);
  return /\b429\b|quota|rate ?limit|too many requests/i.test(msg);
}

function useSheet(configKey: string) {
  const fn = useServerFn(fetchAdsSheet);
  const qc = useQueryClient();
  const ttl = SHEET_TTL_MS[configKey] ?? 10 * 60_000;
  const q = useQuery({
    queryKey: ["ads_sheet", configKey],
    queryFn: () => fn({ data: { configKey } }),
    staleTime: ttl,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
  const refresh = async () => {
    // Clear cached entry and refetch — bypass TTL.
    qc.removeQueries({ queryKey: ["ads_sheet", configKey] });
    await qc.refetchQueries({ queryKey: ["ads_sheet", configKey] });
  };
  return { ...q, refresh };
}

// ----------------- Helpers -----------------
function val(row: Record<string, string>, ...keys: string[]): string {
  for (const k of keys) {
    const lk = k.toLowerCase();
    for (const rk of Object.keys(row)) {
      if (rk.toLowerCase() === lk && row[rk] !== "") return row[rk];
    }
  }
  return "";
}
function parseNum(v: string | number | null | undefined): number {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return v;
  const n = parseFloat(String(v).replace(/[$,%,]/g, ""));
  return Number.isFinite(n) ? n : 0;
}
function num(s: string): number {
  return parseNum(s);
}
function excelSerialToDate(serial: number): Date {
  // Excel serial: days since 1899-12-30 (accounting for leap year bug).
  return new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
}
/** Parse a "Day" cell from CAMP-DAILY / CAMP-HOURLY / DAILY SPENT (Excel serial number). */
function parseExcelDay(v: string): Date | null {
  if (!v) return null;
  const trimmed = String(v).trim();
  // Pure-numeric Excel serial.
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    const n = Number(trimmed);
    if (Number.isFinite(n) && n > 1000 && n < 100000) return excelSerialToDate(n);
  }
  // Fallback: try ISO/date string.
  const d = new Date(trimmed);
  return isNaN(d.getTime()) ? null : d;
}
function parseDDMMYYYY(s: string): Date | null {
  const parts = s.split("/");
  if (parts.length !== 3) return null;
  const [d, m, y] = parts;
  const dt = new Date(Date.UTC(parseInt(y), parseInt(m) - 1, parseInt(d)));
  return isNaN(dt.getTime()) ? null : dt;
}
/** Parse a "Day" cell from Shopify Order or Sheet1 tab — handles YYYY-MM-DD or DD/MM/YYYY. */
function parseOrderDate(v: string): Date | null {
  if (!v) return null;
  const s = String(v).trim();
  if (s.includes("-") && s.startsWith("20")) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  if (s.includes("/")) return parseDDMMYYYY(s);
  // Excel serial fallback (shouldn't normally happen, but be safe).
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    const n = Number(s);
    if (Number.isFinite(n) && n > 1000) return excelSerialToDate(n);
  }
  return null;
}
function isoDay(d: Date): string {
  // Use UTC slice to keep filter comparisons stable regardless of TZ.
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function inRange(d: Date | null, range: DateRange): boolean {
  if (!d) return false;
  const iso = isoDay(d);
  if (range.from && iso < range.from) return false;
  if (range.to && iso > range.to) return false;
  return true;
}
function fmtDay(d: Date | null): string {
  if (!d) return "—";
  return format(d, "dd MMM yyyy");
}
function fmtUsd(n: number): string {
  if (!n) return "—";
  return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}
function fmtUsdStr(s: string): string {
  return fmtUsd(parseNum(s));
}
function fmtNum(n: number): string {
  if (!n) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}
function fmtPctStr(s: string): string {
  if (!s) return "—";
  const n = parseNum(s);
  if (!n) return "—";
  const v = n < 1 ? n * 100 : n;
  return v.toFixed(2) + "%";
}
function dash(s: string): string {
  return s && s.trim() ? s : "—";
}

function StatusHeader({
  configured,
  fetchedAt,
  refresh,
  isFetching,
  error,
  hasData,
  right,
}: {
  configured: boolean;
  fetchedAt?: string;
  refresh: () => void | Promise<void>;
  isFetching: boolean;
  error?: unknown;
  hasData?: boolean;
  right?: React.ReactNode;
}) {
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Auto-hide a 429 notice after 60s by re-rendering when cooldown hits 0.
  const rateLimited = isRateLimitError(error);

  const handleRefresh = async () => {
    if (cooldown > 0 || isFetching) return;
    setCooldown(30);
    try {
      await refresh();
    } catch {
      // swallow — error is already surfaced via query state
    }
  };

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div
          className="ml-auto flex items-center gap-2 text-[12px]"
          style={{ color: FB.muted }}
        >
          {configured && fetchedAt && (
            <span>
              Last synced:{" "}
              <strong style={{ color: FB.text }}>
                {formatDistanceToNow(new Date(fetchedAt), { addSuffix: true })}
              </strong>
            </span>
          )}
          {right}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={cooldown > 0 || isFetching}
            className="inline-flex h-8 items-center gap-1 rounded-md px-3 text-[13px] font-medium transition-colors disabled:opacity-50"
            style={{
              background: FB.card,
              border: `1px solid ${FB.border}`,
              color: FB.text,
            }}
            onMouseEnter={(e) => {
              if (!(cooldown > 0 || isFetching))
                e.currentTarget.style.background = FB.bg;
            }}
            onMouseLeave={(e) => (e.currentTarget.style.background = FB.card)}
          >
            <RefreshCcw className={cn("h-3 w-3", isFetching && "animate-spin")} />
            {cooldown > 0 ? `Refresh (${cooldown}s)` : "Refresh"}
          </button>
        </div>
      </div>
      {rateLimited && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
          {hasData
            ? "Showing previously synced data. Google Sheets API rate limit reached — please wait about a minute before refreshing."
            : "Could not load data — Google Sheets API rate limit reached. Please wait about a minute and click Refresh."}
        </div>
      )}
    </>
  );
}

function EmptyConfig({ label }: { label: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-card p-8 text-center">
      <AlertTriangle className="mx-auto h-6 w-6 text-muted-foreground" />
      <div className="mt-2 text-[13px] font-semibold">{label} sheet not configured</div>
      <p className="mt-1 text-[12px] text-muted-foreground">
        Go to Settings → Ads Performance to add the sheet URL.
      </p>
      <Link
        to="/admin/settings"
        search={{ tab: "ads" } as never}
        className="mt-3 inline-block rounded-md px-4 py-1.5 text-[12px] font-semibold text-white"
        style={{ background: ACCENT }}
      >
        Open Settings
      </Link>
    </div>
  );
}

function ErrorState({ msg, retry }: { msg: string; retry: () => void }) {
  const rateLimited = /\b429\b|quota|rate ?limit|too many requests/i.test(msg);
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
      <div className="text-[13px] font-semibold text-red-700">
        {rateLimited ? "API rate limit reached" : "Could not load sheet data"}
      </div>
      <p className="mt-1 text-[11px] text-red-600">
        {rateLimited
          ? "Google Sheets allows ~60 reads/minute. Please wait about a minute and click Refresh."
          : "Check the sheet URL in Settings or verify the sheet is shared with the connector."}
      </p>
      {!rateLimited && (
        <p className="mt-2 break-all text-[10px] text-red-500">{msg}</p>
      )}
      <Button size="sm" variant="outline" className="mt-3 h-8" onClick={retry}>
        Retry
      </Button>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="h-12 animate-pulse rounded-md border border-border bg-muted/40"
        />
      ))}
    </div>
  );
}

// ----------------- Campaigns Tab -----------------
type AdRow = Record<string, string>;
interface RowTotals {
  spent: number;
  results: number;
  impressions: number;
  clicks: number;
  landingViews: number;
}
interface AdSetGroup {
  name: string;
  ads: AdRow[];
  totals: RowTotals;
  budget: string;
}
interface CampaignGroup {
  name: string;
  id: string;
  adsets: Map<string, AdSetGroup>;
  totals: RowTotals;
  budget: string;
}
interface AccountGroup {
  name: string;
  campaigns: Map<string, CampaignGroup>;
  totals: RowTotals & { campaigns: number };
}

function emptyTotals(): RowTotals {
  return { spent: 0, results: 0, impressions: 0, clicks: 0, landingViews: 0 };
}

function deriveMetrics(t: RowTotals) {
  return {
    cpm: t.impressions > 0 ? (t.spent / t.impressions) * 1000 : 0,
    ctr: t.impressions > 0 ? (t.clicks / t.impressions) * 100 : 0,
    cpc: t.clicks > 0 ? t.spent / t.clicks : 0,
    cpa: t.results > 0 ? t.spent / t.results : 0,
    costPerLpv: t.landingViews > 0 ? t.spent / t.landingViews : 0,
  };
}

function fmtUsd2(n: number): string {
  return n > 0 ? "$" + n.toFixed(2) : "—";
}
function fmtPct2(n: number): string {
  return n > 0 ? n.toFixed(2) + "%" : "—";
}

function buildHierarchy(rows: AdRow[]): AccountGroup[] {
  const accounts = new Map<string, AccountGroup>();
  for (const r of rows) {
    const account = val(r, "Account name") || "(Unknown account)";
    const campName = val(r, "Campaign name") || "(Unknown campaign)";
    const campId = val(r, "Campaign ID");
    const adsetName = val(r, "Adset Name", "Ad Set Name") || "(Unknown ad set)";
    const spent = parseNum(val(r, "Amount spent"));
    const results = parseNum(val(r, "Purchases"));
    const impressions = parseNum(val(r, "Impressions"));
    const clicks = parseNum(val(r, "Clicks (all)"));
    const landingViews = parseNum(val(r, "Landing page views"));
    const campBudget = val(r, "Daily budget");
    const adsetBudget = val(r, "Daily ad set budget");

    let acc = accounts.get(account);
    if (!acc) {
      acc = {
        name: account,
        campaigns: new Map(),
        totals: { ...emptyTotals(), campaigns: 0 },
      };
      accounts.set(account, acc);
    }
    const campKey = campName + "||" + campId;
    let camp = acc.campaigns.get(campKey);
    if (!camp) {
      camp = {
        name: campName,
        id: campId,
        adsets: new Map(),
        totals: emptyTotals(),
        budget: campBudget,
      };
      acc.campaigns.set(campKey, camp);
      acc.totals.campaigns++;
    } else if (!camp.budget && campBudget) {
      camp.budget = campBudget;
    }
    let adset = camp.adsets.get(adsetName);
    if (!adset) {
      adset = {
        name: adsetName,
        ads: [],
        totals: emptyTotals(),
        budget: adsetBudget,
      };
      camp.adsets.set(adsetName, adset);
    } else if (!adset.budget && adsetBudget) {
      adset.budget = adsetBudget;
    }
    adset.ads.push(r);
    for (const t of [adset.totals, camp.totals, acc.totals] as RowTotals[]) {
      t.spent += spent;
      t.results += results;
      t.impressions += impressions;
      t.clicks += clicks;
      t.landingViews += landingViews;
    }
  }
  return Array.from(accounts.values());
}


function CampaignsTab() {
  const [mode, setMode] = useState<"hourly" | "daily">("daily");
  const cfgKey = mode === "hourly" ? "campaigns_hourly" : "campaigns_daily";
  const sheet = useSheet(cfgKey);
  const [range, setRange] = useState<DateRange>(TODAY_RANGE());
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const rows = sheet.data?.rows ?? [];
    return rows.filter((r) => {
      const day = parseExcelDay(val(r, "Day"));
      if ((range.from || range.to) && !inRange(day, range)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = (
          val(r, "Account name") +
          " " +
          val(r, "Campaign name") +
          " " +
          val(r, "Campaign ID")
        ).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [sheet.data, range, search]);

  const groups = useMemo(() => buildHierarchy(filtered), [filtered]);

  return (
    <div>
      <StatusHeader
        configured={sheet.data?.configured ?? false}
        fetchedAt={sheet.data?.fetchedAt}
        refresh={sheet.refresh}
        isFetching={sheet.isFetching}
        error={sheet.error}
        hasData={!!sheet.data}
        right={
          <div
            className="flex h-8 items-center overflow-hidden rounded-md p-0"
            style={{ border: `1px solid ${FB.border}`, background: FB.card }}
          >
            {(["hourly", "daily"] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className="h-8 px-3 text-[13px] font-semibold capitalize transition-colors"
                style={
                  mode === m
                    ? { background: FB.blue, color: "#fff" }
                    : { background: FB.card, color: FB.text }
                }
              >
                {m}
              </button>
            ))}
          </div>
        }
      />
      <AdsDateRangePicker value={range} onChange={setRange} />
      <div
        className="mb-3 flex items-center gap-2 rounded-md px-3 py-1.5"
        style={{ background: FB.card, border: `1px solid ${FB.border}`, boxShadow: FB.shadow }}
      >
        <SearchIcon className="h-3.5 w-3.5" style={{ color: FB.muted }} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search account, campaign name, or campaign ID"
          className="h-7 border-0 p-0 text-[13px] shadow-none focus-visible:ring-0"
          style={{ color: FB.text, background: "transparent" }}
        />
      </div>

      {sheet.isLoading ? (
        <LoadingSkeleton />
      ) : sheet.error && !sheet.data ? (
        <ErrorState msg={String((sheet.error as Error).message)} retry={sheet.refresh} />
      ) : !sheet.data?.configured ? (
        <EmptyConfig label="Campaigns" />
      ) : groups.length === 0 ? (
        <div className="rounded-md border border-border bg-card p-6 text-center text-[12px] text-muted-foreground">
          No rows match the current filters.
        </div>
      ) : (
        <div className="space-y-6" style={{ overflowX: "auto" }}>
          <div style={{ minWidth: CAMPAIGN_MIN_WIDTH }}>
            {groups.map((acc) => (
              <div key={acc.name} style={{ marginBottom: 24 }}>
                <AccountRow acc={acc} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <ChevronRight
      className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")}
      style={{ color: FB.muted }}
    />
  );
}

function HeaderRow() {
  return (
    <div
      className="flex items-center gap-2 py-1.5 pr-3"
      style={{
        background: "#F0F2F5",
        borderTop: `1px solid ${FB.border}`,
        borderBottom: `1px solid ${FB.border}`,
        paddingLeft: 24,
      }}
    >
      <span style={{ width: 14, flex: "none" }} />
      <span
        className="truncate"
        style={{
          position: "sticky",
          left: 0,
          zIndex: 1,
          background: "#F0F2F5",
          minWidth: STICKY_NAME_MIN,
          flex: "none",
          fontSize: 11,
          fontWeight: 600,
          color: FB.muted,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        Campaign / Ad Set / Ad
      </span>
      {CAMPAIGN_COLS.map((c) => (
        <span
          key={c.key}
          style={{
            width: c.w,
            flex: "none",
            textAlign: "right",
            fontSize: 11,
            fontWeight: 600,
            color: FB.muted,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {c.label}
        </span>
      ))}
    </div>
  );
}

function StickyName({
  bg,
  paddingLeft,
  children,
}: {
  bg: string;
  paddingLeft?: number;
  children: React.ReactNode;
}) {
  return (
    <span
      className="truncate"
      style={{
        position: "sticky",
        left: 0,
        zIndex: 1,
        background: bg,
        minWidth: STICKY_NAME_MIN,
        flex: "none",
        paddingLeft,
      }}
    >
      {children}
    </span>
  );
}

function AccountRow({ acc }: { acc: AccountGroup }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="overflow-hidden rounded-lg"
      style={{ border: `1px solid ${FB.border}`, background: FB.card, boxShadow: FB.shadow }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left"
        style={{ background: ACCENT, color: "#fff" }}
      >
        <ChevronRight
          className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")}
          style={{ color: "#fff", opacity: 0.85, flex: "none" }}
        />
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ background: FB.greenDot, flex: "none" }}
        />
        <span
          className="truncate text-[14px] font-bold"
          style={{
            position: "sticky",
            left: 0,
            zIndex: 1,
            background: ACCENT,
            color: "#fff",
            minWidth: STICKY_NAME_MIN,
            flex: "none",
          }}
        >
          {acc.name}
        </span>
        <span className="ml-auto text-[11px]" style={{ color: "#fff", opacity: 0.7, flex: "none" }}>
          {acc.totals.campaigns} campaigns
        </span>
        <span className="text-right text-[13px] font-semibold tabular-nums" style={{ color: "#fff", width: 96, flex: "none" }}>
          {fmtUsd(acc.totals.spent)}
        </span>
        <span className="text-right text-[13px] tabular-nums" style={{ color: "#fff", width: 64, flex: "none" }}>
          {fmtNum(acc.totals.results)}
        </span>
      </button>
      {open && (
        <div style={{ background: FB.card }}>
          <HeaderRow />
          {Array.from(acc.campaigns.values()).map((c) => (
            <CampaignRow key={c.name + c.id} camp={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function CampaignRow({ camp }: { camp: CampaignGroup }) {
  const [open, setOpen] = useState(false);
  const m = deriveMetrics(camp.totals);
  return (
    <div style={{ borderTop: `1px solid ${FB.border}` }}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="group flex w-full items-center gap-2 py-2 pr-3 text-left transition-colors"
        style={{ background: FB.card, paddingLeft: 24 }}
      >
        <Chevron open={open} />
        <StickyName bg={FB.card}>
          <span className="text-[13px] font-semibold" style={{ color: FB.blue }}>
            {camp.name}
          </span>
          {camp.id && (
            <span className="ml-2 font-mono text-[11px]" style={{ color: FB.muted }}>
              {dash(camp.id)}
            </span>
          )}
        </StickyName>
        <Cell w={64}>{fmtNum(camp.totals.results)}</Cell>
        <Cell w={80}>{fmtUsd2(m.cpa)}</Cell>
        <Cell w={88}>{fmtUsd(camp.totals.spent)}</Cell>
        <Cell w={72}>{fmtUsdStr(camp.budget)}</Cell>
        <Cell w={72}>{fmtUsd2(m.cpm)}</Cell>
        <Cell w={64}>{fmtPct2(m.ctr)}</Cell>
        <Cell w={64}>{fmtUsd2(m.cpc)}</Cell>
        <Cell w={64}>{fmtNum(camp.totals.clicks)}</Cell>
        <Cell w={72}>{fmtNum(camp.totals.landingViews)}</Cell>
        <Cell w={80}>{fmtUsd2(m.costPerLpv)}</Cell>
      </button>
      {open && (
        <div>
          {Array.from(camp.adsets.values()).map((a) => (
            <AdSetRow key={a.name} adset={a} />
          ))}
        </div>
      )}
    </div>
  );
}

function AdSetRow({ adset }: { adset: AdSetGroup }) {
  const [open, setOpen] = useState(false);
  const m = deriveMetrics(adset.totals);
  return (
    <div style={{ borderTop: `1px solid ${FB.border}` }}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 py-2 pr-3 text-left transition-colors"
        style={{ background: FB.altRow, paddingLeft: 48 }}
      >
        <Chevron open={open} />
        <StickyName bg={FB.altRow}>
          <span className="text-[13px]" style={{ color: FB.text }}>{adset.name}</span>
        </StickyName>
        <Cell w={64}>{fmtNum(adset.totals.results)}</Cell>
        <Cell w={80}>{fmtUsd2(m.cpa)}</Cell>
        <Cell w={88}>{fmtUsd(adset.totals.spent)}</Cell>
        <Cell w={72}>{fmtUsdStr(adset.budget)}</Cell>
        <Cell w={72}>{fmtUsd2(m.cpm)}</Cell>
        <Cell w={64}>{fmtPct2(m.ctr)}</Cell>
        <Cell w={64}>{fmtUsd2(m.cpc)}</Cell>
        <Cell w={64}>{fmtNum(adset.totals.clicks)}</Cell>
        <Cell w={72}>{fmtNum(adset.totals.landingViews)}</Cell>
        <Cell w={80}>{fmtUsd2(m.costPerLpv)}</Cell>
      </button>
      {open && (
        <div>
          {adset.ads.map((ad, i) => (
            <AdLeafRow key={i} ad={ad} />
          ))}
        </div>
      )}
    </div>
  );
}

function AdLeafRow({ ad }: { ad: AdRow }) {
  return (
    <div
      className="flex items-center gap-2 py-1.5 pr-3 text-left text-[12px]"
      style={{ background: FB.card, paddingLeft: 72, borderTop: `1px solid ${FB.border}`, color: FB.muted }}
    >
      <span style={{ width: 14, flex: "none" }} />
      <StickyName bg={FB.card}>
        <span className="text-[12px]" style={{ color: FB.muted }}>{dash(val(ad, "Ad Name"))}</span>
      </StickyName>
      <Cell w={64}>{fmtNum(parseNum(val(ad, "Purchases")))}</Cell>
      <Cell w={80}>{fmtUsdStr(val(ad, "Cost per purchase"))}</Cell>
      <Cell w={88}>{fmtUsdStr(val(ad, "Amount spent"))}</Cell>
      <Cell w={72}>—</Cell>
      <Cell w={72}>{fmtUsdStr(val(ad, "CPM (cost per 1,000 impressions)"))}</Cell>
      <Cell w={64}>{fmtPctStr(val(ad, "CTR (all)"))}</Cell>
      <Cell w={64}>{fmtUsdStr(val(ad, "CPC (all)"))}</Cell>
      <Cell w={64}>{fmtNum(parseNum(val(ad, "Clicks (all)")))}</Cell>
      <Cell w={72}>{fmtNum(parseNum(val(ad, "Landing page views")))}</Cell>
      <Cell w={80}>{fmtUsdStr(val(ad, "Cost per landing page view"))}</Cell>
    </div>
  );
}

function Cell({
  w,
  mono,
  children,
}: {
  w: number;
  mono?: boolean;
  children: React.ReactNode;
}) {
  const text = typeof children === "string" ? children : "";
  const isEmpty = text === "—" || text === "";
  return (
    <span
      className={cn("shrink-0 text-right text-[13px] tabular-nums", mono && "font-mono")}
      style={{
        width: w,
        flex: "none",
        color: isEmpty ? FB.empty : FB.text,
        fontWeight: isEmpty ? 400 : 500,
      }}
    >
      {children}
    </span>
  );
}

// ----------------- Orders Tab -----------------
interface ProductMapping {
  id: string;
  product_keyword: string;
  campaign_code: string;
  display_name: string | null;
  image_url: string | null;
}

function useProductMappings() {
  return useQuery({
    queryKey: ["product_campaign_mappings"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("product_campaign_mappings")
        .select("*")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as ProductMapping[];
    },
  });
}

function OrdersTab() {
  const daily = useSheet("shopify_orders_daily");
  const triggers = useSheet("shopify_orders_triggers");
  const campaignsHourly = useSheet("campaigns_hourly");
  const campaignsDaily = useSheet("campaigns_daily");
  const [campMode, setCampMode] = useState<"hourly" | "daily">("daily");
  const mappingsQ = useProductMappings();
  const [range, setRange] = useState<DateRange>(TODAY_RANGE());
  const [search, setSearch] = useState("");

  const campaigns = campMode === "hourly" ? campaignsHourly : campaignsDaily;

  // Collect deduped Shopify rows in range.
  const shopifyRows = useMemo(() => {
    const seen = new Set<string>();
    const out: { name: string; store: string; orders: number }[] = [];
    const ingest = (rows: AdRow[]) => {
      for (const r of rows) {
        const rawDay = val(r, "Day");
        const d = parseOrderDate(rawDay);
        if ((range.from || range.to) && !inRange(d, range)) continue;
        const name = val(r, "Product title", "Product Name", "Product", "Title");
        if (!name) continue;
        const store = val(r, "Store name", "Store");
        const orders = parseNum(val(r, "Orders", "Order Count", "Quantity")) || 1;
        const dayKey = d ? isoDay(d) : rawDay;
        const dedupeKey = `${name.toLowerCase()}|${dayKey}|${store.toLowerCase()}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        out.push({ name, store, orders });
      }
    };
    ingest(daily.data?.rows ?? []);
    ingest(triggers.data?.rows ?? []);
    return out;
  }, [daily.data, triggers.data, range]);

  // Meta rows in range.
  const metaRows = useMemo(() => {
    const rows = campaigns.data?.rows ?? [];
    return rows.filter((r) => {
      const day = parseExcelDay(val(r, "Day"));
      if ((range.from || range.to) && !inRange(day, range)) return false;
      return true;
    });
  }, [campaigns.data, range]);

  const cards = useMemo(() => {
    const mappings = mappingsQ.data ?? [];
    type Card = {
      key: string;
      title: string;
      mapping: ProductMapping;
      stores: Set<string>;
      shopifyOrders: number;
      metaPurchases: number;
      metaSpent: number;
      metaImpressions: number;
      metaLpv: number;
      metaClicks: number;
    };
    const cards = new Map<string, Card>();
    const matchedShopify = new Set<number>();

    // Build cards keyed by mapping id.
    for (const m of mappings) {
      cards.set(m.id, {
        key: m.id,
        title: m.display_name?.trim() || m.product_keyword,
        mapping: m,
        stores: new Set(),
        shopifyOrders: 0,
        metaPurchases: 0,
        metaSpent: 0,
        metaImpressions: 0,
        metaLpv: 0,
        metaClicks: 0,
      });
    }

    // Match Shopify rows — first matching mapping wins.
    shopifyRows.forEach((row, idx) => {
      const ln = row.name.toLowerCase();
      for (const m of mappings) {
        if (!m.product_keyword.trim()) continue;
        if (ln.includes(m.product_keyword.toLowerCase())) {
          const c = cards.get(m.id)!;
          c.shopifyOrders += row.orders;
          if (row.store) c.stores.add(row.store);
          matchedShopify.add(idx);
          break;
        }
      }
    });

    // Match Meta rows — first matching mapping wins.
    for (const r of metaRows) {
      const adName = val(r, "Ad name", "Ad Name").toLowerCase();
      const campName = val(r, "Campaign name").toLowerCase();
      for (const m of mappings) {
        const code = m.campaign_code.toLowerCase().trim();
        if (!code) continue;
        if (adName.includes(code) || campName.includes(code)) {
          const c = cards.get(m.id)!;
          c.metaPurchases   += parseNum(val(r, "Purchases"));
          c.metaSpent       += parseNum(val(r, "Amount spent"));
          c.metaImpressions += parseNum(val(r, "Impressions"));
          c.metaClicks      += parseNum(val(r, "Clicks (all)"));
          c.metaLpv         += parseNum(val(r, "Landing page views", "Landing Page Views", "LPV"));
          break;
        }
      }
    }

    let arr = Array.from(cards.values());
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.mapping.product_keyword.toLowerCase().includes(q) ||
          c.mapping.campaign_code.toLowerCase().includes(q),
      );
    }
    arr.sort((a, b) => b.shopifyOrders - a.shopifyOrders);

    const unmatched: string[] = [];
    const seenNames = new Set<string>();
    shopifyRows.forEach((row, idx) => {
      if (matchedShopify.has(idx)) return;
      const k = row.name.toLowerCase();
      if (seenNames.has(k)) return;
      seenNames.add(k);
      unmatched.push(row.name);
    });

    return { cards: arr, unmatched };
  }, [shopifyRows, metaRows, mappingsQ.data, search]);

  const isLoading =
    daily.isLoading ||
    triggers.isLoading ||
    campaigns.isLoading ||
    mappingsQ.isLoading;
  const err = daily.error || triggers.error || campaigns.error;
  const refresh = () => {
    daily.refresh();
    triggers.refresh();
    campaigns.refresh();
    mappingsQ.refetch();
  };
  const anyConfigured =
    (daily.data?.configured ?? false) ||
    (triggers.data?.configured ?? false) ||
    (campaigns.data?.configured ?? false);

  return (
    <div>
      <StatusHeader
        configured={anyConfigured}
        fetchedAt={daily.data?.fetchedAt ?? triggers.data?.fetchedAt}
        refresh={refresh}
        isFetching={
          daily.isFetching || triggers.isFetching || campaigns.isFetching
        }
        error={err}
        hasData={!!(daily.data || triggers.data || campaigns.data)}
        right={
          <div
            className="flex h-8 items-center overflow-hidden rounded-md p-0"
            style={{ border: `1px solid ${FB.border}`, background: FB.card }}
          >
            {(["hourly", "daily"] as const).map((m) => (
              <button
                key={m}
                onClick={() => setCampMode(m)}
                className="h-8 px-3 text-[13px] font-semibold capitalize transition-colors"
                style={
                  campMode === m
                    ? { background: FB.blue, color: "#fff" }
                    : { background: FB.card, color: FB.text }
                }
              >
                Meta {m}
              </button>
            ))}
          </div>
        }
      />
      <AdsDateRangePicker value={range} onChange={setRange} />
      <div
        className="mb-3 flex items-center gap-2 rounded-md px-3 py-1.5"
        style={{ background: FB.card, border: `1px solid ${FB.border}`, boxShadow: FB.shadow }}
      >
        <SearchIcon className="h-3.5 w-3.5" style={{ color: FB.muted }} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by product name or campaign code"
          className="h-7 border-0 p-0 text-[13px] shadow-none focus-visible:ring-0"
          style={{ color: FB.text, background: "transparent" }}
        />
      </div>

      {isLoading ? (
        <LoadingSkeleton />
      ) : err && !(daily.data || triggers.data || campaigns.data) ? (
        <ErrorState msg={String((err as Error).message)} retry={refresh} />
      ) : !anyConfigured ? (
        <EmptyConfig label="Shopify Orders" />
      ) : (mappingsQ.data ?? []).length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-card p-6 text-center text-[12px] text-muted-foreground">
          No product↔campaign mappings configured.{" "}
          <Link
            to="/admin/settings"
            search={{ tab: "ads" } as never}
            className="font-semibold text-foreground underline"
          >
            Configure in Settings
          </Link>
          .
        </div>
      ) : cards.cards.length === 0 ? (
        <div className="rounded-md border border-border bg-card p-6 text-center text-[12px] text-muted-foreground">
          No products match the current filters.
        </div>
      ) : (
        <>
          <OrdersSummaryBar cards={cards.cards} />
          <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-4">
            {cards.cards.map((c) => {
              const cpm = c.metaImpressions > 0 ? (c.metaSpent / c.metaImpressions) * 1000 : null;
              const cpc = c.metaClicks > 0 ? c.metaSpent / c.metaClicks : null;
              const ctr = c.metaImpressions > 0 ? (c.metaClicks / c.metaImpressions) * 100 : null;
              const cpp = c.metaPurchases > 0 ? c.metaSpent / c.metaPurchases : null;
              return (
                <ProductCard
                  key={c.key}
                  title={c.title}
                  imageUrl={c.mapping.image_url}
                  stores={Array.from(c.stores)}
                  shopifyOrders={c.shopifyOrders}
                  metaPurchases={c.metaPurchases}
                  metaSpent={c.metaSpent}
                  metaImpressions={c.metaImpressions}
                  metaLpv={c.metaLpv}
                  metaClicks={c.metaClicks}
                  cpm={cpm}
                  cpc={cpc}
                  ctr={ctr}
                  cpp={cpp}
                  campaignCode={c.mapping.campaign_code}
                />
              );
            })}
          </div>
          {cards.unmatched.length > 0 && (
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-800">
              <div className="font-semibold">
                {cards.unmatched.length} product
                {cards.unmatched.length === 1 ? "" : "s"} have no campaign
                mapping.{" "}
                <Link
                  to="/admin/settings"
                  search={{ tab: "ads" } as never}
                  className="underline"
                >
                  Configure in Settings → Ads Performance
                </Link>
                .
              </div>
              <div className="mt-1 text-amber-700">
                {cards.unmatched
                  .slice(0, 12)
                  .map((n) => (n.length > 50 ? n.slice(0, 50) + "…" : n))
                  .join(" · ")}
                {cards.unmatched.length > 12
                  ? ` · +${cards.unmatched.length - 12} more`
                  : ""}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

type SummaryCard = {
  shopifyOrders: number;
  metaPurchases: number;
  metaSpent: number;
  metaImpressions: number;
  metaClicks: number;
  metaLpv: number;
};

function OrdersSummaryBar({ cards }: { cards: SummaryCard[] }) {
  const t = cards.reduce(
    (a, c) => {
      a.shopifyOrders   += c.shopifyOrders;
      a.metaPurchases   += c.metaPurchases;
      a.metaSpent       += c.metaSpent;
      a.metaImpressions += c.metaImpressions;
      a.metaClicks      += c.metaClicks;
      a.metaLpv         += c.metaLpv;
      return a;
    },
    { shopifyOrders: 0, metaPurchases: 0, metaSpent: 0, metaImpressions: 0, metaClicks: 0, metaLpv: 0 },
  );
  const cpm = t.metaImpressions > 0 ? (t.metaSpent / t.metaImpressions) * 1000 : null;
  const cpc = t.metaClicks > 0 ? t.metaSpent / t.metaClicks : null;
  const ctr = t.metaImpressions > 0 ? (t.metaClicks / t.metaImpressions) * 100 : null;
  const cpp = t.metaPurchases > 0 ? t.metaSpent / t.metaPurchases : null;
  const tiles: { label: string; value: string }[] = [
    { label: "Total Shopify Orders", value: fmtNum(t.shopifyOrders) },
    { label: "Total Meta Orders", value: fmtNum(t.metaPurchases) },
    { label: "Total Spend", value: fmtUsd(t.metaSpent) },
    { label: "Cost / Purchase", value: cpp != null ? fmtUsd(cpp) : "—" },
    { label: "Avg CPM", value: cpm != null ? fmtUsd(cpm) : "—" },
    { label: "Avg CPC", value: cpc != null ? fmtUsd(cpc) : "—" },
    { label: "Avg CTR", value: ctr != null ? ctr.toFixed(2) + "%" : "—" },
    { label: "Total Impressions", value: fmtNum(t.metaImpressions) },
    { label: "Total Landing Views", value: fmtNum(t.metaLpv) },
  ];
  const colorFor = (label: string) => {
    if (label === "Total Shopify Orders") return FB.darkShopify;
    if (label === "Total Meta Orders") return FB.darkMeta;
    return "#FFFFFF";
  };
  const sizeFor = (label: string) =>
    label === "Total Shopify Orders" || label === "Total Meta Orders"
      ? { fontSize: 22, fontWeight: 800 }
      : { fontSize: 20, fontWeight: 700 };
  return (
    <div
      className="mb-3 flex flex-wrap items-stretch overflow-hidden rounded-lg shadow-sm"
      style={{ background: ACCENT }}
    >
      {tiles.map((tile, i) => (
        <div
          key={tile.label}
          className={cn("flex-1 px-4 py-3", i > 0 && "border-l border-white/10")}
          style={{ minWidth: 130 }}
        >
          <div
            className="text-[10px] font-semibold uppercase tracking-wide"
            style={{ color: FB.darkLabel }}
          >
            {tile.label}
          </div>
          <div
            className="mt-0.5 tabular-nums"
            style={{ color: colorFor(tile.label), ...sizeFor(tile.label), lineHeight: 1.1 }}
          >
            {tile.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProductCard({
  title,
  imageUrl,
  stores,
  shopifyOrders,
  metaPurchases,
  metaSpent,
  metaImpressions,
  metaLpv,
  metaClicks,
  cpm,
  cpc,
  ctr,
  cpp,
  campaignCode,
}: {
  title: string;
  imageUrl?: string | null;
  stores: string[];
  shopifyOrders: number;
  metaPurchases: number;
  metaSpent: number;
  metaImpressions: number;
  metaLpv: number;
  metaClicks: number;
  cpm: number | null;
  cpc: number | null;
  ctr: number | null;
  cpp: number | null;
  campaignCode: string;
}) {
  const initials = title
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  const [imgErr, setImgErr] = useState(false);
  const showImage = !!imageUrl && !imgErr;
  return (
    <div
      className="overflow-hidden rounded-lg"
      style={{
        background: FB.card,
        border: `1px solid ${FB.border}`,
        boxShadow: FB.cardShadow,
      }}
    >
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: "16 / 9", background: FB.bg, borderRadius: "8px 8px 0 0" }}
      >
        {showImage ? (
          <img
            src={imageUrl!}
            alt={title}
            className="h-full w-full object-cover"
            onError={() => setImgErr(true)}
          />
        ) : (
          <div
            className="flex h-full w-full flex-col items-center justify-center gap-1"
            style={{ color: FB.muted }}
          >
            <ImageOff className="h-5 w-5" />
            <span className="text-[20px] font-bold">{initials || "—"}</span>
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div
            className="text-[16px] font-bold leading-tight"
            style={{ color: FB.text }}
          >
            {title}
          </div>
          <span
            className="shrink-0 font-mono text-[11px] font-semibold"
            style={{
              background: FB.blueSoft,
              color: FB.blue,
              borderRadius: 4,
              padding: "2px 6px",
            }}
          >
            {campaignCode}
          </span>
        </div>
        {stores.length > 0 && (
          <div className="mt-1 text-[12px]" style={{ color: FB.muted }}>
            {stores.join(", ")}
          </div>
        )}
      </div>
      <div
        className="grid grid-cols-2"
        style={{
          gap: 12,
          padding: 16,
          borderTop: `1px solid ${FB.border}`,
        }}
      >
        <BigOrderTile label="Shopify Orders" value={fmtNum(shopifyOrders)} color={FB.green} />
        <BigOrderTile label="Meta Orders" value={fmtNum(metaPurchases)} color={FB.blue} />
        <Pill label="Total Spend" value={fmtUsd(metaSpent)} />
        <Pill label="Cost / Purchase" value={cpp != null ? fmtUsd(cpp) : "—"} />
        <Pill label="CPM" value={cpm != null ? fmtUsd(cpm) : "—"} />
        <Pill label="CPC" value={cpc != null ? fmtUsd(cpc) : "—"} />
        <Pill label="CTR" value={ctr != null ? ctr.toFixed(2) + "%" : "—"} />
        <Pill label="Clicks" value={fmtNum(metaClicks)} />
        <Pill label="Landing Views" value={fmtNum(metaLpv)} />
        <Pill label="Impressions" value={fmtNum(metaImpressions)} />
      </div>
    </div>
  );
}

function BigOrderTile({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  const isEmpty = value === "—" || value === "0";
  return (
    <div>
      <div
        className="font-semibold uppercase"
        style={{ fontSize: 10, color: FB.muted, letterSpacing: "0.3px" }}
      >
        {label}
      </div>
      <div
        className="mt-1 tabular-nums"
        style={{
          fontSize: 28,
          fontWeight: 800,
          lineHeight: 1,
          color: isEmpty ? FB.empty : color,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Pill({ label, value }: { label: string; value: string }) {
  const isEmpty = value === "—";
  return (
    <div>
      <div
        className="font-semibold uppercase"
        style={{ fontSize: 10, color: FB.muted, letterSpacing: "0.3px" }}
      >
        {label}
      </div>
      <div
        className="mt-1 tabular-nums"
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: isEmpty ? FB.empty : FB.text,
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ----------------- Daily Spent Tab -----------------
function DailySpentTab() {
  const sheet = useSheet("ad_spent_daily");
  const [range, setRange] = useState<DateRange>(TODAY_RANGE());
  const [search, setSearch] = useState("");

  const grouped = useMemo(() => {
    const rows = sheet.data?.rows ?? [];
    const filtered = rows.filter((r) => {
      const day = parseExcelDay(val(r, "Day"));
      if ((range.from || range.to) && !inRange(day, range)) return false;
      const name = val(r, "Account name");
      if (search.trim() && !name.toLowerCase().includes(search.toLowerCase()))
        return false;
      return true;
    });
    type DayRow = { date: Date | null; spent: number; raw: AdRow };
    const map = new Map<string, { name: string; days: DayRow[]; total: number }>();
    for (const r of filtered) {
      const name = val(r, "Account name") || "(Unknown)";
      const day = parseExcelDay(val(r, "Day"));
      const spent = parseNum(val(r, "Amount spent"));
      let g = map.get(name);
      if (!g) {
        g = { name, days: [], total: 0 };
        map.set(name, g);
      }
      g.days.push({ date: day, spent, raw: r });
      g.total += spent;
    }
    return Array.from(map.values()).map((g) => ({
      ...g,
      days: g.days.sort((a, b) => {
        const at = a.date ? a.date.getTime() : 0;
        const bt = b.date ? b.date.getTime() : 0;
        return bt - at;
      }),
    }));
  }, [sheet.data, range, search]);

  const totalAll = grouped.reduce((acc, g) => acc + g.total, 0);

  return (
    <div>
      <StatusHeader
        configured={sheet.data?.configured ?? false}
        fetchedAt={sheet.data?.fetchedAt}
        refresh={sheet.refresh}
        isFetching={sheet.isFetching}
        error={sheet.error}
        hasData={!!sheet.data}
      />
      <AdsDateRangePicker value={range} onChange={setRange} />
      <div
        className="mb-3 flex items-center gap-2 rounded-md px-3 py-1.5"
        style={{ background: FB.card, border: `1px solid ${FB.border}`, boxShadow: FB.shadow }}
      >
        <SearchIcon className="h-3.5 w-3.5" style={{ color: FB.muted }} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search account name"
          className="h-7 border-0 p-0 text-[13px] shadow-none focus-visible:ring-0"
          style={{ color: FB.text, background: "transparent" }}
        />
      </div>

      <div
        className="mb-3 rounded-lg px-4 py-3"
        style={{
          background: FB.card,
          border: `1px solid ${FB.border}`,
          borderLeft: `4px solid ${FB.blue}`,
          boxShadow: FB.shadow,
        }}
      >
        <div
          className="font-semibold uppercase"
          style={{ fontSize: 10, color: FB.muted, letterSpacing: "0.3px" }}
        >
          Total spent across all accounts
        </div>
        <div
          className="mt-1 tabular-nums"
          style={{ fontSize: 24, fontWeight: 800, color: FB.text }}
        >
          {fmtUsd(totalAll)}
        </div>
      </div>

      {sheet.isLoading ? (
        <LoadingSkeleton />
      ) : sheet.error && !sheet.data ? (
        <ErrorState msg={String((sheet.error as Error).message)} retry={sheet.refresh} />
      ) : !sheet.data?.configured ? (
        <EmptyConfig label="Ad Spent" />
      ) : grouped.length === 0 ? (
        <div className="rounded-md border border-border bg-card p-6 text-center text-[12px] text-muted-foreground">
          No data.
        </div>
      ) : (
        <div className="space-y-2">
          {grouped.map((g) => (
            <SpentAccountRow
              key={g.name}
              name={g.name}
              days={g.days}
              total={g.total}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SpentAccountRow({
  name,
  days,
  total,
}: {
  name: string;
  days: { date: Date | null; spent: number }[];
  total: number;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="overflow-hidden rounded-lg"
      style={{ border: `1px solid ${FB.border}`, background: FB.card, boxShadow: FB.shadow }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left"
        style={{ background: ACCENT, color: "#fff" }}
      >
        <ChevronRight
          className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")}
          style={{ color: "#fff", opacity: 0.85 }}
        />
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ background: FB.greenDot }}
        />
        <span className="flex-1 text-[14px] font-bold" style={{ color: "#fff" }}>
          {name}
        </span>
        <span className="text-[11px]" style={{ color: "#fff", opacity: 0.7 }}>
          {days.length} days
        </span>
        <span
          className="w-28 text-right text-[13px] font-bold tabular-nums"
          style={{ color: "#fff" }}
        >
          {fmtUsd(total)}
        </span>
      </button>
      {open && (
        <div style={{ background: FB.card }}>
          <div
            className="grid grid-cols-2 gap-2 px-4 py-2 font-semibold uppercase"
            style={{
              fontSize: 11,
              color: FB.muted,
              letterSpacing: "0.3px",
              background: FB.altRow,
              borderBottom: `1px solid ${FB.border}`,
            }}
          >
            <span>Date</span>
            <span className="text-right">Spent</span>
          </div>
          {days.map((d, i) => (
            <div
              key={i}
              className="grid grid-cols-2 gap-2 px-4 py-2 tabular-nums"
              style={{
                fontSize: 13,
                color: FB.text,
                borderBottom: i === days.length - 1 ? "none" : `1px solid ${FB.border}`,
              }}
            >
              <span>{fmtDay(d.date)}</span>
              <span className="text-right" style={{ fontWeight: 600 }}>
                {fmtUsd(d.spent)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
